import React, { useEffect, useId, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { MermaidContainer, MermaidError, MermaidPre } from "./style";

interface MermaidBlockProps {
  code: string;
}

const MermaidBlock: React.FC<MermaidBlockProps> = ({ code }) => {
  const theme = useTheme();
  const reactId = useId();
  const diagramId = `mermaid-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [svg, setSvg] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const trimmed = code?.trim();
    if (!trimmed) {
      setSvg(null);
      setRenderError(null);
      return;
    }

    (async () => {
      try {
        const mermaid = (await import("mermaid")).default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: theme.palette.mode === "dark" ? "dark" : "default",
          fontFamily: "inherit",
        });
        await mermaid.parse(trimmed);
        const { svg: renderedSvg } = await mermaid.render(diagramId, trimmed);
        if (!cancelled) {
          setSvg(renderedSvg);
          setRenderError(null);
        }
      } catch (err: any) {
        if (cancelled) return;
        document
          .querySelectorAll(`[id^="${diagramId}"]`)
          .forEach((n) => n.parentElement?.removeChild(n));
        setSvg(null);
        setRenderError(err?.message ?? String(err));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, diagramId, theme.palette.mode]);

  if (renderError) {
    return (
      <MermaidContainer>
        <MermaidError>{renderError}</MermaidError>
        <MermaidPre>{code}</MermaidPre>
      </MermaidContainer>
    );
  }

  if (!svg) {
    return (
      <MermaidContainer>
        <MermaidPre>{code}</MermaidPre>
      </MermaidContainer>
    );
  }

  return (
    <MermaidContainer
      ref={containerRef}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

export default MermaidBlock;
